import { keyProxy } from '@qre-mailing/smart-table';
import { createContext, FC, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { useQueryParam } from './hooks/useQueryParam';

type LanguageContextType = {
  language: string;
  setLanguage: (language: string) => void;
};

const defaultLanguage = 'en-GB';

const LanguageContext = createContext<LanguageContextType>({
  language: defaultLanguage,
  setLanguage: () => {
    /* no-op fallback: provider may be temporarily missing during HMR */
  },
});

const localStorageLanguageProxy = keyProxy('language');

export const LanguageContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const languageFromLocalStorage = localStorageLanguageProxy.get();
  const { value: languageFromQueryParam, clear: clearLang } = useQueryParam('lang');

  useEffect(
    () => {
      if (languageFromQueryParam) {
        localStorageLanguageProxy.set(languageFromQueryParam);
        clearLang();
      }
    }
    /* no dependencies here on purpose */
  );

  const [language, setLanguage] = useState(languageFromQueryParam || languageFromLocalStorage || defaultLanguage);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
    }),
    [language]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguageContext = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguageContext must be used within a LanguageContextProvider');
  }

  return context;
};
